import React, { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { PROGRAMS } from "@/data/programs";
import {
  ArrowRight, ArrowLeft, Send, CheckCircle2,
  BookOpen, Clock, Users, Award, ChevronDown,
} from "lucide-react";

const slugFromParams = (params) => {
  const seg1 = params.segment1;
  const seg2 = params.segment2;
  return seg2 ? `${seg1}/${seg2}` : seg1;
};

const CYCLE_COLORS = {
  bba: { accent: "#002362", light: "#EEF2FA", label: "Cycle 1 — BBA" },
  mba: { accent: "#ED2939", light: "#FEF2F2", label: "Cycle 2 — MBA" },
  "mba-executive": { accent: "#C0392B", light: "#FDECEA", label: "Executive" },
};

const getCycleColor = (slug) => {
  if (slug?.startsWith("bba")) return CYCLE_COLORS.bba;
  if (slug?.startsWith("mba-executive")) return CYCLE_COLORS["mba-executive"];
  if (slug?.startsWith("mba")) return CYCLE_COLORS.mba;
  return CYCLE_COLORS.bba;
};

export default function ProgramDetailPage() {
  const params = useParams();
  const slug = slugFromParams(params);
  const program = PROGRAMS[slug];
  const [openSection, setOpenSection] = useState(0);

  if (!program) return <Navigate to="/formations" replace />;

  const cycle = getCycleColor(slug);

  return (
    <div className="min-h-screen bg-white">

      {/* ── HERO ── */}
      <section className="relative pt-28 pb-0 overflow-hidden" style={{ background: cycle.accent }}>
        {/* Dot grid background */}
        <svg className="absolute inset-0 w-full h-full opacity-[0.07] pointer-events-none" preserveAspectRatio="none">
          <defs>
            <pattern id="hero-dots" width="24" height="24" patternUnits="userSpaceOnUse">
              <circle cx="2" cy="2" r="1.5" fill="white" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#hero-dots)" />
        </svg>

        {/* Glow orb */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full blur-[120px] opacity-20"
          style={{ background: "radial-gradient(circle, rgba(255,255,255,0.3), transparent)" }} />

        <div className="container-edge relative z-10 pb-16 md:pb-24">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-white/50 mb-10">
            <Link to="/" className="hover:text-white transition-colors">Accueil</Link>
            <span>/</span>
            <Link to="/formations" className="hover:text-white transition-colors">Formations</Link>
            <span>/</span>
            <span className="text-white/80">{program.eyebrow}</span>
          </nav>

          <div className="grid grid-cols-12 gap-8 items-end">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="col-span-12 lg:col-span-7"
            >
              {/* Cycle badge */}
              <span className="inline-block text-[10px] uppercase tracking-[0.22em] font-semibold px-3 py-1.5 mb-6 border border-white/20 text-white/70">
                {cycle.label}
              </span>

              <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white leading-[1.0] mb-4">
                {program.title}{" "}
                {program.italic && (
                  <em className="not-italic" style={{ color: "rgba(255,255,255,0.6)" }}>
                    {program.italic}
                  </em>
                )}
              </h1>

              <p className="text-white/70 text-[16px] leading-relaxed max-w-2xl mt-5">
                {program.hero_subtitle}
              </p>

              {/* Key info pills */}
              <div className="flex flex-wrap gap-3 mt-8">
                {program.key_info?.slice(0, 3).map((k) => (
                  <div key={k.label} className="flex items-center gap-2 bg-white/10 border border-white/15 px-4 py-2">
                    <span className="text-white/50 text-[10px] uppercase tracking-[0.18em]">{k.label}</span>
                    <span className="text-white text-[13px] font-semibold">{k.value}</span>
                  </div>
                ))}
              </div>

              {/* CTAs */}
              <div className="flex flex-wrap gap-3 mt-8">
                <Link to="/contact#candidater" className="inline-flex items-center gap-2 bg-white text-navy-700 px-6 py-3 text-[11px] uppercase tracking-[0.18em] font-semibold hover:bg-white/90 transition-colors">
                  Candidater
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/admissions" className="inline-flex items-center gap-2 border border-white/30 text-white px-6 py-3 text-[11px] uppercase tracking-[0.18em] font-semibold hover:bg-white/10 transition-colors">
                  Process d'admission
                </Link>
              </div>
            </motion.div>

            {/* Tagline card */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="col-span-12 lg:col-span-5 hidden lg:block"
            >
              <div className="bg-white/10 border border-white/15 p-8">
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/50 mb-4">Programme</div>
                <div className="font-display text-3xl text-white leading-tight mb-6">{program.tagline}</div>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { icon: Clock, label: program.key_info?.[0]?.label, val: program.key_info?.[0]?.value },
                    { icon: Award, label: program.key_info?.[1]?.label, val: program.key_info?.[1]?.value },
                  ].map((item, i) => item.label && (
                    <div key={i} className="flex items-start gap-3">
                      <item.icon className="h-4 w-4 text-white/40 mt-0.5 shrink-0" strokeWidth={1.5} />
                      <div>
                        <div className="text-[10px] text-white/40 uppercase tracking-[0.14em]">{item.label}</div>
                        <div className="text-white text-[13px] font-semibold mt-0.5">{item.val}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Wave bottom */}
        <div className="relative h-12 md:h-16">
          <svg viewBox="0 0 1440 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="absolute bottom-0 w-full" preserveAspectRatio="none">
            <path d="M0 64L1440 64L1440 20C1200 60 960 0 720 20C480 40 240 0 0 20L0 64Z" fill="white" />
          </svg>
        </div>
      </section>

      {/* ── MAIN CONTENT ── */}
      <section className="section-padding bg-white">
        <div className="container-edge">
          <div className="grid grid-cols-12 gap-10 lg:gap-16">

            {/* Left: sections */}
            <div className="col-span-12 lg:col-span-8">

              {/* Program image */}
              {program.image && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="relative mb-14 aspect-[16/7] overflow-hidden"
                >
                  <div className="absolute -top-3 -left-3 w-12 h-12 border-t-2 border-l-2 z-10" style={{ borderColor: cycle.accent }} />
                  <div className="absolute -bottom-3 -right-3 w-12 h-12 border-b-2 border-r-2 border-navy-500 z-10" />
                  <img src={program.image} alt={program.eyebrow} className="w-full h-full object-cover" />
                </motion.div>
              )}

              {/* Sections cards */}
              <div className="space-y-4">
                {program.sections?.map((s, i) => (
                  <motion.div
                    key={s.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.5, delay: i * 0.08 }}
                    className="group relative bg-white border border-navy-100 overflow-hidden isolate cursor-pointer"
                    onClick={() => setOpenSection(openSection === i ? -1 : i)}
                    data-testid={`program-section-${i}`}
                  >
                    {/* Hover gradient */}
                    <div
                      className={`absolute inset-0 origin-bottom transition-transform duration-700 ease-[cubic-bezier(0.65,0,0.35,1)] -z-10 ${openSection === i ? "scale-y-100" : "scale-y-0 group-hover:scale-y-100"}`}
                      style={{ background: `linear-gradient(135deg, ${cycle.accent}, #000e27)` }}
                    />

                    {/* Dot pattern */}
                    <svg className={`absolute inset-0 w-full h-full pointer-events-none transition-opacity duration-700 ${openSection === i ? "opacity-[0.06]" : "opacity-0 group-hover:opacity-[0.06]"}`} preserveAspectRatio="none">
                      <defs>
                        <pattern id={`sec-pat-${i}`} width="16" height="16" patternUnits="userSpaceOnUse">
                          <circle cx="2" cy="2" r="1" fill="white" />
                        </pattern>
                      </defs>
                      <rect width="100%" height="100%" fill={`url(#sec-pat-${i})`} />
                    </svg>

                    {/* Big number */}
                    <span
                      className={`absolute -top-2 right-4 font-display text-[130px] leading-none select-none pointer-events-none transition-colors duration-500 ${openSection === i ? "text-white/[0.08]" : "text-navy-700/[0.06] group-hover:text-white/[0.08]"}`}
                      aria-hidden
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>

                    {/* Header */}
                    <div className="relative flex items-center justify-between p-6 md:p-8">
                      <h2 className={`font-display text-xl md:text-2xl transition-colors duration-500 ${openSection === i ? "text-white" : "text-navy-700 group-hover:text-white"}`}>
                        {s.title}
                      </h2>
                      <ChevronDown className={`h-5 w-5 shrink-0 ml-4 transition-all duration-300 ${openSection === i ? "rotate-180 text-white" : "text-navy-400 group-hover:text-white"}`} />
                    </div>

                    {/* Content */}
                    {openSection === i && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        transition={{ duration: 0.3 }}
                        className="relative px-6 md:px-8 pb-8 border-t border-white/20"
                      >
                        <div className="pt-5">
                          {s.content && (
                            <p className="text-white/80 text-[15px] leading-[1.75]">{s.content}</p>
                          )}
                          {s.list && (
                            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3 mt-4">
                              {s.list.map((item) => (
                                <li key={item} className="flex gap-3 text-white/80 text-[14px]">
                                  <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5 text-white/50" />
                                  {item}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </motion.div>
                    )}

                    {/* Tricolor stripe */}
                    <div className="absolute bottom-0 left-0 right-0 h-[3px] flex overflow-hidden">
                      <span className={`flex-1 bg-[#002362] origin-left transition-transform duration-500 ${openSection === i ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"}`} />
                      <span className={`flex-1 bg-white origin-left transition-transform duration-[600ms] ${openSection === i ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"}`} />
                      <span className={`flex-1 bg-[#ED2939] origin-left transition-transform duration-700 ${openSection === i ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"}`} />
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Years navigation (parent pages) */}
              {program.years && (
                <div className="mt-14">
                  <div className="overline mb-6">Choisissez votre année</div>
                  <div className={`grid gap-5 ${program.years.length === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
                    {program.years.map((y, i) => (
                      <motion.div
                        key={y.slug}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: i * 0.1 }}
                      >
                        <Link
                          to={`/formations/${y.slug}`}
                          className="group relative flex flex-col overflow-hidden bg-white border border-navy-100 hover:shadow-xl transition-all duration-500 isolate"
                          data-testid={`year-link-${y.slug.replace("/", "-")}`}
                          style={{ minHeight: "200px" }}
                        >
                          {/* Hover gradient */}
                          <div
                            className="absolute inset-0 origin-bottom transition-transform duration-700 ease-[cubic-bezier(0.65,0,0.35,1)] -z-10 scale-y-0 group-hover:scale-y-100"
                            style={{ background: `linear-gradient(135deg, ${cycle.accent}, #000e27)` }}
                          />

                          {/* Glow */}
                          <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 bg-white/20" />

                          {/* Big number */}
                          <span className="absolute -top-2 right-3 font-display text-[120px] leading-none select-none pointer-events-none text-navy-700/[0.06] group-hover:text-white/[0.08] transition-colors duration-500" aria-hidden>
                            {String(i + 1).padStart(2, "0")}
                          </span>

                          <div className="relative p-7 flex flex-col h-full">
                            {/* Level */}
                            <div
                              className="text-[10px] uppercase tracking-[0.22em] font-semibold mb-3 transition-colors duration-500 group-hover:text-white/60"
                              style={{ color: cycle.accent }}
                            >
                              {y.level}
                            </div>

                            {/* Title */}
                            <h3 className="font-display text-2xl md:text-3xl text-navy-700 group-hover:text-white transition-colors duration-500 mb-auto">
                              {y.label}
                            </h3>

                            {/* CTA */}
                            <div className="flex items-center gap-2 mt-6 text-[11px] uppercase tracking-[0.2em] font-semibold text-navy-500 group-hover:text-white transition-colors duration-500">
                              Voir le programme
                              <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform duration-300" />
                            </div>
                          </div>

                          {/* Tricolor stripe */}
                          <div className="absolute bottom-0 left-0 right-0 h-[3px] flex overflow-hidden">
                            <span className="flex-1 bg-[#002362] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
                            <span className="flex-1 bg-white origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-[600ms]" />
                            <span className="flex-1 bg-[#ED2939] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-700" />
                          </div>
                        </Link>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}

              {/* Back to parent */}
              {program.parent_slug && (
                <div className="mt-12 pt-8 border-t border-navy-100">
                  <Link to={`/formations/${program.parent_slug}`} className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-semibold text-navy-500 hover:text-navy-700 transition-colors" data-testid="back-to-parent">
                    <ArrowLeft className="h-4 w-4" />
                    Vue d'ensemble du programme
                  </Link>
                </div>
              )}
            </div>

            {/* ── SIDEBAR ── */}
            <aside className="col-span-12 lg:col-span-4">
              <div className="lg:sticky lg:top-28 space-y-5">

                {/* Key info */}
                <div className="border border-navy-100 overflow-hidden">
                  <div className="px-6 py-4 border-b border-navy-100" style={{ background: cycle.light }}>
                    <div className="overline" style={{ color: cycle.accent }}>En bref</div>
                  </div>
                  <ul className="divide-y divide-navy-50">
                    {program.key_info?.map((k) => (
                      <li key={k.label} className="flex justify-between items-start gap-4 px-6 py-4">
                        <span className="text-[#666] text-[13px]">{k.label}</span>
                        <span className="text-navy-700 font-semibold text-[13px] text-right">{k.value}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA block */}
                <div className="p-6 border border-navy-100 relative overflow-hidden">
                  <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: cycle.accent }} />
                  <div className="overline mb-1" style={{ color: cycle.accent }}>Intéressé(e) ?</div>
                  <h3 className="font-display text-xl text-navy-700 mb-3">Parlons de votre projet</h3>
                  <p className="text-[#555] text-[13px] leading-relaxed mb-5">
                    Notre équipe admissions vous répond sous 48h.
                  </p>
                  <div className="flex flex-col gap-2.5">
                    <Link to="/contact#candidater" className="btn-red w-full justify-center" data-testid="program-sidebar-cta">
                      <Send className="h-4 w-4" />
                      Candidater
                    </Link>
                    <Link to="/admissions" className="btn-outline-navy w-full justify-center">
                      Process d'admission
                    </Link>
                  </div>
                </div>

                {/* Rythmes */}
                <div className="p-6 border border-navy-100 bg-[#FAF8F3]">
                  <div className="overline mb-4">Rythmes disponibles</div>
                  <div className="flex flex-wrap gap-2">
                    {["Initial", "Alternance", "E-learning"].map((r) => (
                      <span key={r} className="px-3 py-1.5 text-[11px] uppercase tracking-[0.16em] border border-navy-200 text-navy-700 bg-white font-semibold">
                        {r}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* ── ATOUTS ── */}
      {program.perks && (
        <section className="section-padding" style={{ background: cycle.light }}>
          <div className="container-edge">
            <div className="overline mb-2" style={{ color: cycle.accent }}>Les atouts ECL</div>
            <h2 className="font-display text-2xl md:text-4xl text-navy-700 mb-10">
              Ce que vous gagnez en rejoignant ECL
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {program.perks.map((p, i) => (
                <motion.div
                  key={p.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="bg-white p-8 border border-navy-100 relative overflow-hidden group hover:shadow-md transition-shadow"
                >
                  <div className="absolute top-0 left-0 w-1 h-full transition-all duration-500" style={{ background: cycle.accent }} />
                  <div className="font-display text-5xl md:text-6xl leading-none mb-3" style={{ color: cycle.accent }}>{p.stat}</div>
                  <div className="overline text-navy-500 mb-2">{p.label}</div>
                  <p className="text-[#555] text-[14px] leading-relaxed">{p.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── BOTTOM CTA ── */}
      <section className="py-16 md:py-20" style={{ background: cycle.accent }}>
        <div className="container-edge text-center">
          <div className="text-white/60 text-[11px] uppercase tracking-[0.22em] mb-4">Prêt(e) ?</div>
          <h2 className="font-display text-3xl md:text-5xl text-white mb-6">
            Rejoignez {program.eyebrow}
          </h2>
          <p className="text-white/70 text-[16px] max-w-xl mx-auto mb-8">
            Les candidatures sont ouvertes en continu. Postulez dès maintenant pour sécuriser votre place.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/contact#candidater" className="inline-flex items-center gap-2 bg-white px-8 py-4 text-[12px] uppercase tracking-[0.2em] font-semibold hover:bg-white/90 transition-colors" style={{ color: cycle.accent }}>
              Candidater maintenant
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/formations" className="inline-flex items-center gap-2 border border-white/30 text-white px-8 py-4 text-[12px] uppercase tracking-[0.2em] font-semibold hover:bg-white/10 transition-colors">
              Voir toutes les formations
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
}
